import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getPlaceHours, type PlaceHoursQuery } from "@/features/addresses/addresses.api";
import { WeekBar, useHoursWords } from "./OpeningHoursEditor";
import { describeOpeningHours, type OpeningHours } from "./openingHours";

// What HERE knows of the opening hours of the business at a new address — or at a place that
// exists, when the planner asks for it on its hours tab (23-09-2026). Only as a proposal: they
// are the hours of the business as the public sees them,
// not necessarily when a lorry may load or unload. *Overnemen* puts them in the editor in one
// click and the planner corrects them there (Camiel, 22-09-2026: "kun je de openingstijden dan
// ook laten overnemen"); typing them over by hand stays possible. Asked once the name and the
// place stand still for a moment; quiet when HERE is off or down, as every address lookup is.
//
// Either it looks them up for the address that is being typed (`query`), or the business was
// picked from the place search and brought its hours along (`given`).

const WAIT_MS = 700;

function ready(query: PlaceHoursQuery): boolean {
  return query.name.trim().length >= 3 && Boolean(query.city?.trim() || query.postalCode?.trim() || (query.lat != null && query.lng != null));
}

export function HereHoursHint({
  query,
  given,
  onTake,
}: {
  query: PlaceHoursQuery;
  /** The business picked in the place search, with the hours it came with. */
  given?: { title: string; label: string; hours: OpeningHours } | null;
  /** Present: the proposal gets a button that puts it in the editor. */
  onTake?: (hours: OpeningHours) => void;
}) {
  const { t } = useTranslation();
  const words = useHoursWords();
  const key = JSON.stringify(query);
  const [settled, setSettled] = useState(key);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(key), WAIT_MS);
    return () => clearTimeout(timer);
  }, [key]);
  const asked: PlaceHoursQuery = JSON.parse(settled);

  const answer = useQuery({
    queryKey: ["addresses", "place-hours", settled],
    queryFn: () => getPlaceHours(asked),
    // A business picked in the place search brought its hours with it: no second question.
    enabled: !given && ready(asked),
    staleTime: 60 * 60_000,
    retry: false,
  });

  const place = given ?? answer.data?.place;
  if (!given) {
    if (!ready(asked) || answer.isError) return null;
    if (answer.isLoading) return <span className="t-caption t-muted">{t("locations.hereHoursLooking")}</span>;
    if (!answer.data?.found || !place) return <span className="t-caption t-muted">{t("locations.hereHoursNone")}</span>;
  }
  if (!place) return null;
  const distance = "distanceMeters" in place ? place.distanceMeters : null;

  return (
    <div className="wl-alert wl-alert--info" style={{ flexDirection: "column", alignItems: "stretch", gap: "var(--space-1)" }}>
      <span className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)" }}>
        {t("locations.hereHoursTitle", { place: place.title })}
      </span>
      <span className="t-caption">
        {[place.label, distance ? t("locations.hereHoursDistance", { meters: distance }) : null].filter(Boolean).join(" · ")}
      </span>
      <span className="t-body-sm">{describeOpeningHours(place.hours, words)}</span>
      {place.hours.status === "known" && <WeekBar hours={place.hours} words={words} />}
      <div className="wl-hstack" style={{ gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
        {onTake && (
          <button type="button" className="wl-btn wl-btn--secondary wl-btn--sm" onClick={() => onTake(place.hours)}>
            {t("locations.hereHoursTake")}
          </button>
        )}
        <span className="t-caption">{t("locations.hereHoursHelp")}</span>
      </div>
    </div>
  );
}
